import React, { useState, useEffect } from 'react'
import {
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Popover,
    TextField,
    Theme,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import FlagIcon from '@material-ui/icons/Flag'

import { Container } from '../Container'

interface CreateStatusPopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    onClose: () => void
    onSave: (name: string, color: string, causesFlag: boolean) => void
    theme: Theme
    initialName?: string
    initialColor?: string
    initialCausesFlag?: boolean
}

export const CreateStatusPopover = (props: CreateStatusPopoverProps) => {
    const {
        open,
        anchorEl,
        onClose,
        onSave,
        theme: propTheme,
        initialName = '',
        initialColor = '#000000',
        initialCausesFlag = false,
    } = props

    const theme = propTheme || useTheme()
    const [statusName, setStatusName] = useState(initialName)
    const [statusColor, setStatusColor] = useState(initialColor)
    const [statusCausesFlag, setStatusCausesFlag] = useState(initialCausesFlag)

    // Update local state when props change
    useEffect(() => {
        setStatusName(initialName)
        setStatusColor(initialColor)
        setStatusCausesFlag(initialCausesFlag)
    }, [initialName, initialColor, initialCausesFlag, open])

    // Reset form when popover closes
    useEffect(() => {
        if (!open) {
            setStatusName('')
            setStatusColor('#000000')
            setStatusCausesFlag(false)
        }
    }, [open])

    const handleSave = () => {
        if (!statusName.trim()) {
            return // Don't save if name is empty
        }
        onSave(statusName, statusColor, statusCausesFlag)
        onClose()
    }

    return (
        <Popover
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={open}
            PaperProps={{
                style: {
                    borderRadius: theme.shape.borderRadius * 2,
                    boxShadow: theme.shadows[8],
                },
            }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disablePortal={false}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    padding: theme.spacing(3),
                    minWidth: '350px',
                }}
            >
                <Container
                    style={{
                        fontWeight: 600,
                        fontSize: '20px',
                        marginBottom: theme.spacing(3),
                        color: theme.palette.text.primary,
                    }}
                >
                    Create New Status
                </Container>

                <TextField
                    label="Status Name"
                    value={statusName}
                    onChange={(e) => setStatusName(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: theme.spacing(3) }}
                    required
                />

                <Container
                    style={{
                        flexDirection: 'column',
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <Container
                        style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            marginBottom: theme.spacing(1.5),
                        }}
                    >
                        Color
                    </Container>
                    <Container
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: theme.spacing(2),
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <Container
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                            }}
                        >
                            <div
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    backgroundColor: statusColor,
                                    border: `3px solid ${theme.palette.grey[400]}`,
                                    borderRadius: theme.shape.borderRadius,
                                    boxShadow: theme.shadows[4],
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                                onClick={() => {
                                    const input = document.createElement(
                                        'input',
                                    )
                                    input.type = 'color'
                                    input.value = statusColor
                                    input.onchange = (e: any) => {
                                        setStatusColor(e.target.value)
                                    }
                                    input.click()
                                }}
                            >
                                <input
                                    type="color"
                                    value={statusColor}
                                    onChange={(e) =>
                                        setStatusColor(e.target.value)
                                    }
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        opacity: 0,
                                        cursor: 'pointer',
                                    }}
                                />
                            </div>
                            <span
                                style={{
                                    fontSize: '11px',
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                }}
                            >
                                Click to Pick
                            </span>
                        </Container>
                        <Container
                            style={{
                                flexDirection: 'column',
                                flex: 1,
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <TextField
                                label="Hex Color"
                                value={statusColor.toUpperCase()}
                                onChange={(e) => {
                                    const value = e.target.value.replace(
                                        /[^0-9A-Fa-f]/g,
                                        '',
                                    )
                                    if (value.length <= 6) {
                                        setStatusColor(
                                            value.length === 6
                                                ? `#${value}`
                                                : value.length > 0
                                                ? `#${value}`
                                                : '#000000',
                                        )
                                    }
                                }}
                                variant="outlined"
                                size="small"
                                placeholder="#000000"
                                inputProps={{
                                    maxLength: 7,
                                    style: {
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <span
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                marginRight: theme.spacing(0.5),
                                            }}
                                        >
                                            #
                                        </span>
                                    ),
                                }}
                            />
                            <Container
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: theme.spacing(1),
                                }}
                            >
                                {[
                                    '#FF0000',
                                    '#00FF00',
                                    '#0000FF',
                                    '#FFFF00',
                                    '#FF00FF',
                                    '#00FFFF',
                                    '#FFA500',
                                    '#800080',
                                    '#FFC0CB',
                                    '#A52A2A',
                                    '#808080',
                                    '#000000',
                                ].map((color) => (
                                    <div
                                        key={color}
                                        onClick={() => setStatusColor(color)}
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                            backgroundColor: color,
                                            border: `2px solid ${
                                                statusColor.toUpperCase() ===
                                                color
                                                    ? theme.palette.primary.main
                                                    : theme.palette.grey[400]
                                            }`,
                                            borderRadius:
                                                theme.shape.borderRadius,
                                            cursor: 'pointer',
                                            boxShadow:
                                                statusColor.toUpperCase() ===
                                                color
                                                    ? theme.shadows[4]
                                                    : theme.shadows[1],
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform =
                                                'scale(1.1)'
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform =
                                                'scale(1)'
                                        }}
                                    />
                                ))}
                            </Container>
                        </Container>
                    </Container>
                </Container>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={statusCausesFlag}
                            onChange={(e) =>
                                setStatusCausesFlag(e.target.checked)
                            }
                            color="primary"
                        />
                    }
                    label={
                        <Container
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(0.5),
                            }}
                        >
                            <span>Causes Flag</span>
                            <Tooltip title="When checked, this status will cause a flag to be raised">
                                <FlagIcon
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            </Tooltip>
                        </Container>
                    }
                    style={{ marginBottom: theme.spacing(3) }}
                />

                <Divider style={{ marginBottom: theme.spacing(2) }} />

                <Container
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: theme.spacing(1),
                    }}
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={onClose}
                        style={{
                            textTransform: 'none',
                            padding: theme.spacing(0.75, 2),
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={!statusName.trim()}
                        style={{
                            textTransform: 'none',
                            padding: theme.spacing(0.75, 2),
                        }}
                        startIcon={<SaveIcon />}
                    >
                        Create
                    </Button>
                </Container>
            </Container>
        </Popover>
    )
}
